/**
 * OrderDetailsTab Component
 * Shows order details, tests ordered, instructions, and next steps
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  FlaskConical,
  MapPin,
  Home,
  FileText,
  Phone,
  Mail,
  Calendar,
} from 'lucide-react';
import { PatientLabOrder } from '@/services/patientLabService';
import { format, parseISO } from 'date-fns';

interface OrderDetailsTabProps {
  order: PatientLabOrder;
  sessionToken: string;
}

const OrderDetailsTab: React.FC<OrderDetailsTabProps> = ({ order }) => {
  const collectionMethodLabel =
    order.collectionMethod === 'walk_in'
      ? 'Walk-in at Quest Diagnostics'
      : 'At-home phlebotomy';

  const getNextSteps = () => {
    switch (order.status) {
      case 'pending_appointment':
        return [
          'Go to the Appointment tab to schedule your at-home phlebotomy appointment',
          'Or visit the Lab Centers tab to find a walk-in location',
        ];
      case 'requisition_ready':
        return [
          'Download your requisition form using the button below',
          'Visit the selected Quest location with your requisition',
          'Bring a valid ID',
        ];
      case 'appointment_scheduled':
        return [
          'Your appointment is scheduled',
          'A phlebotomist will arrive at your address on the scheduled date',
          'Please be available at the scheduled time',
        ];
      case 'sample_collected':
      case 'processing':
        return [
          'Your sample has been collected',
          'Results are being processed',
          'You will be notified when results are ready',
        ];
      case 'results_ready':
      case 'completed':
        return [
          'Your results are ready',
          'View them in the Results tab',
          'Your provider will review and contact you if needed',
        ];
      default:
        return ['Please wait for further instructions'];
    }
  };

  return (
    <div className="space-y-4">
      {/* Tests Ordered */}
      <Card className="border-slate-700 bg-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-blue-400" />
            Tests Ordered
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {order.panel ? (
            <div>
              <p className="font-medium">{order.panel.name}</p>
              {order.tests.length > 0 && (
                <p className="text-sm text-slate-400 mt-1">
                  {order.tests.length} test{order.tests.length !== 1 ? 's' : ''} included
                </p>
              )}
            </div>
          ) : order.tests.length > 0 ? (
            <div>
              <p className="font-medium mb-2">
                {order.tests.length} test{order.tests.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex flex-wrap gap-2">
                {order.tests.map((testId, idx) => (
                  <Badge key={idx} variant="secondary">
                    Test {idx + 1}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No tests specified</p>
          )}
        </CardContent>
      </Card>

      {/* Collection Method */}
      <Card className="border-slate-700 bg-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {order.collectionMethod === 'walk_in' ? (
              <MapPin className="h-5 w-5 text-blue-400" />
            ) : (
              <Home className="h-5 w-5 text-blue-400" />
            )}
            Collection Method
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-medium">{collectionMethodLabel}</p>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card className="border-slate-700 bg-slate-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-400" />
            Next Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {getNextSteps().map((step, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-blue-400 mt-0.5">•</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Requisition Download */}
      {order.requisitionUrl && (
        <Card className="border-slate-700 bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-400" />
              Requisition Form
            </CardTitle>
          </CardHeader>
          <CardContent>
            <a
              href={order.requisitionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 underline"
            >
              <FileText className="h-4 w-4" />
              Download Requisition PDF
            </a>
          </CardContent>
        </Card>
      )}

      {/* Important Dates */}
      <Card className="border-slate-700 bg-slate-800">
        <CardHeader>
          <CardTitle className="text-lg">Important Dates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">Order Created:</span>
            <span>
              {format(parseISO(order.createdAt), 'MMM d, yyyy h:mm a')}
            </span>
          </div>
          {order.metadata?.deadlineDate && (
            <div className="flex justify-between">
              <span className="text-slate-400">Deadline:</span>
              <span className="text-yellow-400 font-medium">
                {format(
                  parseISO(order.metadata.deadlineDate as string),
                  'MMM d, yyyy'
                )}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderDetailsTab;
